import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import { AxiosError } from 'axios';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import { useCollectionClient } from '../../clients/hooks/useCollectionClient';
import { buildEmailPreviewHtml } from '../../shared/buildEmailPreviewHtml';
import { useForwardEmail, ForwardEmailResponse } from '../hooks/useForwardEmail';
import { CollectionEmailSendDetail } from '../model';

interface Props {
  open: boolean;
  email: CollectionEmailSendDetail;
  onClose: () => void;
  onSent: () => void;
}

// Union of original recipients + all client contacts, deduped case-insensitively (original casing kept).
const buildRecipientPool = (originalTo: string[], contactEmails: string[]): string[] => {
  const seen = new Map<string, string>();
  [...originalTo, ...contactEmails].forEach((raw) => {
    const value = raw.trim();
    const key = value.toLowerCase();
    if (key && !seen.has(key)) seen.set(key, value);
  });
  return Array.from(seen.values());
};

const ForwardEmailModal: React.FC<Props> = ({ open, email, onClose, onSent }) => {
  const { showSuccessSnackBar, showErrorSnackBar, showInfoSnackBar } = useContext(SnackBarContext);
  const { forwardEmail, isLoading } = useForwardEmail();
  const { data: clientResp } = useCollectionClient(email.collections_client_id, open);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const recipientPool = useMemo(() => {
    const client = clientResp?.data;
    const contactEmails = [...(client?.contacts ?? []), ...(client?.statement_contacts ?? [])].flatMap(
      (c) => c.emails ?? [],
    );
    return buildRecipientPool(email.to_emails ?? [], contactEmails);
  }, [clientResp, email.to_emails]);

  // Pre-select the original recipients each time the modal opens.
  useEffect(() => {
    if (open) setSelected(new Set((email.to_emails ?? []).map((e) => e.trim().toLowerCase())));
  }, [open, email.to_emails]);

  const toggle = (value: string) => {
    const key = value.trim().toLowerCase();
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectedEmails = recipientPool.filter((e) => selected.has(e.trim().toLowerCase()));
  const canSend = selectedEmails.length > 0 && !isLoading;

  const handleSend = async () => {
    try {
      const res = await forwardEmail({
        emailSendId: email.id,
        toEmails: selectedEmails,
        location: email.location,
      });
      const skipped = (res.data as ForwardEmailResponse).skipped_attachments ?? [];
      showSuccessSnackBar?.('Email forwarded.');
      if (skipped.length > 0) showInfoSnackBar?.(`Some invoices were no longer available: ${skipped.join(', ')}`);
      onSent();
    } catch (err) {
      const axiosErr = err as AxiosError<{ error?: string }>;
      showErrorSnackBar?.(axiosErr.response?.data?.error || 'Failed to forward email. Please try again.');
    }
  };

  return (
    <Dialog open={open} onClose={isLoading ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Forward email</Typography>
        <IconButton onClick={onClose} size="small" disabled={isLoading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
          <strong>Recipients</strong> — select any client contact:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
          {recipientPool.length > 0 ? (
            recipientPool.map((addr) => {
              const isSelected = selected.has(addr.trim().toLowerCase());
              return (
                <Chip
                  key={addr}
                  label={addr}
                  size="small"
                  color={isSelected ? 'primary' : 'default'}
                  variant={isSelected ? 'filled' : 'outlined'}
                  onClick={() => toggle(addr)}
                  disabled={isLoading}
                />
              );
            })
          ) : (
            <Chip label="No contacts available" size="small" color="error" variant="outlined" />
          )}
        </Box>

        <Typography variant="body2" color="textSecondary">
          <strong>Subject:</strong> {email.subject}
        </Typography>
        {email.attachment_metadata && email.attachment_metadata.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="textSecondary">
              <strong>Attachments (kept):</strong>
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {email.attachment_metadata.map((a) => (
                <Chip key={a.edoc_invoice_import_id} label={a.filename} size="small" variant="outlined" />
              ))}
            </Box>
          </Box>
        )}

        {/* email.body is server-generated HTML; sandbox="" is defense-in-depth for the read-only preview. */}
        <iframe
          srcDoc={buildEmailPreviewHtml(email.body, email.sender_display_name, email.reply_to_email)}
          sandbox=""
          title="Email preview"
          style={{ width: '100%', height: 360, border: '1px solid #e0e0e0', borderRadius: 4, marginTop: 12 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={isLoading ? <CircularProgress size={16} /> : <ForwardToInboxIcon />}
          onClick={handleSend}
          disabled={!canSend}
        >
          Forward
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ForwardEmailModal;
